import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, HeartHandshake, ClipboardCheck, Users } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { api, BRAND_IMAGE } from "@/lib/api";
import { CTABanner } from "@/components/blocks";

const PILLARS = [
  {
    icon: ClipboardCheck,
    title: "Professionally prepared",
    body: "We take time to understand each nanny's experience and readiness before introducing them to a family.",
  },
  {
    icon: HeartHandshake,
    title: "Considered matching",
    body: "Every family is different. We match on temperament, schedule and the care your children actually need.",
  },
  {
    icon: ShieldCheck,
    title: "Handled with care",
    body: "Your details are kept private and reviewed by our team — never listed publicly or shared carelessly.",
  },
];

export default function Home() {
  useSeo({
    title: "Nanny Poppins UAE — Professionally Prepared Nannies for UAE Families",
    description:
      "Nanny Poppins thoughtfully matches families across the UAE with professionally prepared nannies. Request a match or apply to be represented.",
  });

  const [brandImg, setBrandImg] = useState(BRAND_IMAGE);

  useEffect(() => {
    api
      .get("/config")
      .then((res) => res.data?.brand_image_url && setBrandImg(res.data.brand_image_url))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero / brand panel */}
      <section className="bg-[#0B0A08] text-[#F7F4ED]" data-testid="home-hero">
        <div className="container-page grid items-center gap-12 pb-16 pt-8 md:pb-24 md:pt-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="eyebrow fade-up">Nanny matching · United Arab Emirates</p>
            <h1 className="fade-up mt-5 font-serif text-4xl leading-[1.03] tracking-tight sm:text-5xl lg:text-6xl">
              Thoughtful care, matched with intention.
            </h1>
            <p className="fade-up mt-6 max-w-xl text-lg leading-relaxed text-[#F7F4ED]/70">
              We help families across the Emirates find professionally prepared nannies — through a calm,
              personal process rather than an anonymous directory.
            </p>
            <div className="fade-up mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-a-nanny-match"
                data-testid="hero-request-match"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#C6A04A] px-7 py-3.5 text-sm font-semibold text-[#0B0A08] transition-transform hover:scale-[1.02]"
              >
                Request a Nanny Match
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/how-it-works"
                data-testid="hero-how-it-works"
                className="inline-flex items-center justify-center rounded-full border border-[#F7F4ED]/25 px-7 py-3.5 text-sm font-semibold text-[#F7F4ED] transition-colors hover:border-[#C6A04A] hover:text-[#C6A04A]"
              >
                How it works
              </Link>
            </div>
          </div>

          <div className="fade-up flex justify-center lg:justify-end">
            <div className="w-full max-w-sm rounded-2xl border border-[#C6A04A]/25 bg-black p-3">
              <img
                src={brandImg}
                alt="Nanny Poppins — golden emblem of a nanny holding an umbrella"
                data-testid="brand-panel-image"
                className="h-auto w-full rounded-xl object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro / positioning */}
      <section className="container-page py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
          <div className="lg:col-span-1">
            <p className="eyebrow">Why Nanny Poppins</p>
            <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
              A quieter, more careful way to find care.
            </h2>
          </div>
          <div className="prose-editorial lg:col-span-2">
            <p className="text-lg">
              Choosing who cares for your children is a deeply personal decision. We approach it the same way —
              listening first, then introducing nannies we believe genuinely fit your household. No public
              profiles, no pressure, and no promises we can't keep.
            </p>
            <p className="mt-5 text-[#0B0A08]/70">
              On fees: the team will discuss fees after understanding the family's needs.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-y border-[#0B0A08]/10 bg-white">
        <div className="container-page grid gap-8 py-16 md:grid-cols-3 md:py-20">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-[#0B0A08]/10 p-8 transition-shadow hover:shadow-[0_8px_30px_rgba(11,10,8,0.05)]"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#C6A04A]/12 text-[#C6A04A]">
                <p.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 font-serif text-xl tracking-tight">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#0B0A08]/70">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Two paths */}
      <section className="container-page py-16 md:py-24">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="flex flex-col rounded-2xl border border-[#0B0A08]/10 bg-white p-10">
            <Users className="h-7 w-7 text-[#C6A04A]" />
            <h3 className="mt-5 font-serif text-2xl tracking-tight">For families</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#0B0A08]/70">
              Tell us about your household and the care you're looking for. We'll review your request and be in
              touch to talk through next steps.
            </p>
            <Link
              to="/request-a-nanny-match"
              data-testid="home-family-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0B0A08] hover:text-[#C6A04A]"
            >
              Request a nanny match <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="flex flex-col rounded-2xl border border-[#0B0A08]/10 bg-white p-10">
            <HeartHandshake className="h-7 w-7 text-[#C6A04A]" />
            <h3 className="mt-5 font-serif text-2xl tracking-tight">For nannies</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#0B0A08]/70">
              If you're an experienced nanny looking to be represented, we'd like to hear from you. Your
              application is kept private to our team.
            </p>
            <Link
              to="/for-nannies"
              data-testid="home-nanny-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0B0A08] hover:text-[#C6A04A]"
            >
              Apply to be represented <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
